// Lead-source grouping for the dashboard's Lead Sources donut and Lead Source Trend.
//
// Labels are the server's reporting channels (`leadAttribution.bySource` / `byMonth`).
// Each named channel owns a fixed chart colour so a source looks the same in both
// charts and keeps its colour when the period changes. The order is the stack order
// and was checked for colour-blind separation between neighbours. Any label not
// listed here is grouped as "Other" — AI Assistant and Website Form are listed, so
// they always get their own slice.
export const LEAD_SOURCE_SERIES = [
  { label: 'Google', color: 'green' },
  { label: 'Facebook', color: 'retail' },
  { label: 'Instagram', color: 'red' },
  { label: 'AI Assistant', color: 'purple' },
  { label: 'Website Form', color: 'amber' },
  { label: 'Organic (calls/texts)', color: 'blue' },
  { label: 'Dealer', color: 'orange' },
];
export const OTHER_LABEL = 'Other';
export const OTHER_COLOR = 'grey';

const NAMED = new Set(LEAD_SOURCE_SERIES.map(s => s.label));

/**
 * Donut slices from `bySource` ({ label: { total, converted, revenue } }), in fixed
 * series order. Unlisted labels are summed into "Other", whose sublabel names them.
 * @returns {Array<{ name, value, colorKey, sublabel? }>}
 */
export function buildLeadSourceSlices(bySource) {
  const entries = Object.entries(bySource || {});
  const slices = LEAD_SOURCE_SERIES
    .filter(s => entries.some(([label, d]) => label === s.label && d.total > 0))
    .map(s => ({ name: s.label, value: bySource[s.label].total, colorKey: s.color }));

  const others = entries
    .filter(([label, d]) => !NAMED.has(label) && d.total > 0)
    .sort((a, b) => b[1].total - a[1].total);
  if (others.length > 0) {
    slices.push({
      name: OTHER_LABEL,
      value: others.reduce((sum, [, d]) => sum + d.total, 0),
      colorKey: OTHER_COLOR,
      sublabel: others.map(([label, d]) => `${label} ${d.total}`).join(' · '),
    });
  }
  return slices;
}

/**
 * Monthly rows from `byMonth` ({ "YYYY-MM": { label: count } }), oldest first, with
 * months that had no leads filled in as zero so the axis has no gaps.
 * @returns {{ rows: Array<{ month, [label]: count }>, series: Array<{ label, colorKey }> }}
 */
export function buildLeadSourceMonths(byMonth) {
  const months = Object.keys(byMonth || {}).filter(m => /^\d{4}-\d{2}$/.test(m)).sort();
  if (months.length === 0) return { rows: [], series: [] };

  const allMonths = [];
  let [y, m] = months[0].split('-').map(Number);
  const last = months[months.length - 1];
  for (;;) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    allMonths.push(key);
    if (key >= last) break;
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }

  const seen = new Set();
  const rows = allMonths.map(month => {
    const row = { month };
    Object.entries(byMonth[month] || {}).forEach(([label, count]) => {
      const key = NAMED.has(label) ? label : OTHER_LABEL;
      row[key] = (row[key] || 0) + (Number(count) || 0);
      seen.add(key);
    });
    return row;
  });

  const series = LEAD_SOURCE_SERIES
    .filter(s => seen.has(s.label))
    .map(s => ({ label: s.label, colorKey: s.color }));
  if (seen.has(OTHER_LABEL)) series.push({ label: OTHER_LABEL, colorKey: OTHER_COLOR });

  // Recharts stacks undefined as a gap; give every series an explicit 0.
  rows.forEach(row => series.forEach(s => { if (row[s.label] == null) row[s.label] = 0; }));
  return { rows, series };
}
